/**
 * Report settings and deterministic backend-report preparation.
 *
 * This dialog never prints the DOM or takes a browser screenshot. It sends the
 * canonical project/result to the backend report engine and previews only the
 * report configuration and qualification state.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { reportService } from '../../api/client';
import { CheckboxField, Field, MessageList, SelectField } from '../../shared/components';
import type {
  CalculationResponse,
  Project,
  ReportSection,
  ReportSectionSelection,
} from '../../gen/ejot/roofing/v1/types';
import { buildReportPreviewSummary } from './reportPreview';

const SCALE_OPTIONS = [
  { value: '0', label: 'Fit each drawing to its frame' },
  { value: '50', label: '1:50' },
  { value: '100', label: '1:100' },
  { value: '200', label: '1:200' },
  { value: '500', label: '1:500' },
  { value: '1000', label: '1:1000' },
];

const DECIMAL_OPTIONS = [
  { value: '.', label: 'Point (1.5)' },
  { value: ',', label: 'Comma (1,5)' },
];

interface ReportDialogProps {
  open: boolean;
  project: Project;
  result: CalculationResponse | undefined;
  sections: readonly ReportSection[];
  onClose: () => void;
}

export function ReportDialog({ open, project, result, sections, onClose }: ReportDialogProps) {
  const mandatoryIds = useMemo(
    () => sections.filter((section) => section.mandatory).map((section) => section.id ?? ''),
    [sections],
  );
  const defaultIds = useMemo(
    () => sections.filter((section) => section.selected_by_default || section.mandatory).map((section) => section.id ?? ''),
    [sections],
  );

  const [selected, setSelected] = useState<string[]>(defaultIds);
  const [includeDrawings, setIncludeDrawings] = useState(true);
  const [scale, setScale] = useState('0');
  const [decimalSeparator, setDecimalSeparator] = useState('.');
  const [showPageNumbers, setShowPageNumbers] = useState(true);
  const [reportTitle, setReportTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [downloadUrl, setDownloadUrl] = useState<string>();

  useEffect(() => {
    if (defaultIds.length > 0) setSelected(defaultIds);
  }, [defaultIds]);

  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) dialogRef.current?.focus();
  }, [open]);

  useEffect(() => () => {
    if (downloadUrl) URL.revokeObjectURL(downloadUrl);
  }, [downloadUrl]);

  const preview = useMemo(
    () => buildReportPreviewSummary(result, sections, selected, includeDrawings),
    [result, sections, selected, includeDrawings],
  );

  const download = useMutation({
    mutationFn: async (artifactId: string) => {
      const blob = await reportService.downloadReport(artifactId);
      return URL.createObjectURL(blob);
    },
    onSuccess: (nextUrl) => {
      if (downloadUrl) URL.revokeObjectURL(downloadUrl);
      setDownloadUrl(nextUrl);
    },
  });

  const generate = useMutation({
    mutationFn: () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
        setDownloadUrl(undefined);
      }
      const selection: ReportSectionSelection = {
        included_section_ids: selected,
        include_drawings: includeDrawings,
        preferred_scale_denominator: Number(scale) || 0,
      };
      return reportService.generateReport({
        project,
        result,
        sections: selection,
        metadata: {
          report_title: reportTitle || undefined,
          report_date: new Date().toISOString().slice(0, 10),
          decimal_separator: decimalSeparator,
          show_page_numbers: showPageNumbers,
          additional_notes: notes || undefined,
        },
      });
    },
  });

  if (!open) return null;

  const artifact = generate.data?.artifact;
  const toggleSection = (id: string, on: boolean) => {
    setSelected((previous) => (
      on ? [...new Set([...previous, id])] : previous.filter((sectionId) => sectionId !== id)
    ));
  };

  return (
    <div className="dialog-backdrop" role="presentation" onClick={onClose}>
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-dialog-title"
        tabIndex={-1}
        ref={dialogRef}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => { if (event.key === 'Escape') onClose(); }}
      >
        <div className="dialog-header">
          <h2 className="dialog-title" id="report-dialog-title">Report</h2>
          <button type="button" className="header-button" onClick={onClose}>Close</button>
        </div>

        <div className="dialog-body">
          {!result && (
            <p className="field-note field-note-blocking" role="status">
              No calculation result is available. A report generated now records the project
              inputs and the conditions that prevented a calculation. It is not a verification.
            </p>
          )}

          <section className="dialog-group report-preview" aria-labelledby="report-preview-title">
            <h3 id="report-preview-title">Report preview</h3>
            <dl className="definition-list">
              <dt>Calculation status</dt><dd>{preview.statusLabel}</dd>
              <dt>Resistance basis</dt><dd>{preview.basisLabel}</dd>
              <dt>Modules / zones</dt><dd>{preview.moduleCount} / {preview.zoneCount}</dd>
              <dt>Included sections</dt><dd>{preview.selectedSectionCount}</dd>
              <dt>Drawings</dt><dd>{preview.drawingMode}</dd>
            </dl>
            <p className="field-note">{preview.qualification}</p>
          </section>

          <fieldset className="dialog-group">
            <legend>Sections</legend>
            {sections.map((section) => {
              const id = section.id ?? '';
              const mandatory = Boolean(section.mandatory);
              return (
                <CheckboxField
                  key={id}
                  label={section.display_name + (mandatory ? ' (always included)' : '')}
                  checked={mandatory || selected.includes(id)}
                  onChange={mandatory ? undefined : (on) => toggleSection(id, on)}
                />
              );
            })}
            {mandatoryIds.length > 0 && (
              <p className="field-note">
                Sections marked as always included cannot be removed. A report must carry the
                statements that qualify its own results.
              </p>
            )}
          </fieldset>

          <fieldset className="dialog-group">
            <legend>Drawings</legend>
            <CheckboxField
              label="Include backend-generated drawings"
              checked={includeDrawings}
              onChange={setIncludeDrawings}
            />
            <SelectField
              label="Drawing scale"
              value={scale}
              options={SCALE_OPTIONS}
              onChange={setScale}
            />
            <p className="field-note">
              A requested scale is used only when the drawing fits at it. Otherwise the backend
              drawing engine fits the scene and states the actual scale. Browser screenshots are never used.
            </p>
          </fieldset>

          <fieldset className="dialog-group">
            <legend>Presentation</legend>
            <Field label="Report title" value={reportTitle} onChange={setReportTitle} />
            <SelectField
              label="Decimal separator"
              value={decimalSeparator}
              options={DECIMAL_OPTIONS}
              onChange={setDecimalSeparator}
            />
            <CheckboxField
              label="Show page numbers"
              checked={showPageNumbers}
              onChange={setShowPageNumbers}
            />
            <Field label="Notes" value={notes} onChange={setNotes} />
            <p className="field-note">
              The report is written in English and metric units for this release. User notes do not change calculation data.
            </p>
          </fieldset>

          {generate.isError && (
            <p className="field-note field-note-blocking" role="alert">
              The report could not be generated: {String(generate.error)}
            </p>
          )}

          {generate.data?.messages && generate.data.messages.length > 0 && (
            <MessageList messages={generate.data.messages} emptyText="" />
          )}

          {artifact && (
            <div className="dialog-result" role="status" aria-live="polite">
              <p className="field-note">
                {artifact.page_count} page{artifact.page_count === 1 ? '' : 's'},{' '}
                {Math.round(Number(artifact.size_bytes ?? 0) / 1024)} kB.
              </p>
              <p className="dialog-hash">{artifact.content_hash}</p>
              {!downloadUrl && (
                <button
                  type="button"
                  className="header-button header-button-primary"
                  onClick={() => download.mutate(artifact.id ?? '')}
                  disabled={download.isPending}
                >
                  {download.isPending ? 'Preparing' : 'Prepare the download'}
                </button>
              )}
              {downloadUrl && (
                <a
                  className="header-button header-button-primary"
                  href={downloadUrl}
                  download={artifact.file_name}
                >
                  Download the report
                </a>
              )}
              {download.isError && (
                <p className="field-note field-note-blocking" role="alert">
                  The report could not be downloaded: {String(download.error)}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="dialog-footer">
          <button type="button" className="header-button" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="header-button header-button-primary"
            onClick={() => generate.mutate()}
            disabled={generate.isPending}
          >
            {generate.isPending ? 'Generating' : 'Generate the report'}
          </button>
        </div>
      </div>
    </div>
  );
}
